import { Dialog, DialogTitle, DialogContent, Box, Button } from '@mui/material';
import Paper, { PaperProps } from '@mui/material/Paper';
import React, { useMemo, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';

import api from '../api/axios';

import ReservationForm, { Mode } from './ReservationForm';

/** Paper del Dialog arrastrable + redimensionable con identidad estable
 *  Importante: no se escribe en .current; se usa useImperativeHandle.
 */
const ReservationDialogPaper = React.forwardRef<HTMLDivElement, PaperProps>(
  function ReservationDialogPaper(props, ref) {
    // ref interno que usará react-draggable
    const nodeRef = React.useRef<HTMLDivElement>(null!);

    // Expone el mismo nodo al ref externo del Dialog (MUI)
    React.useImperativeHandle(ref, () => nodeRef.current, []);

    return (
      <Draggable
        nodeRef={nodeRef}
        handle="#reservation-dialog-title" // arrastra desde el título
        cancel={[
          '[class*="MuiDialogContent-root"]',
          '[class*="MuiAutocomplete-root"]',
          'input',
          'textarea',
          'select',
          '[contenteditable="true"]',
        ].join(', ')} // evita drag al interactuar con el contenido
        bounds="body"
      >
        <Paper
          ref={nodeRef}
          {...props}
          sx={{
            ...props?.sx,
            resize: 'both', // permite redimensionar
            overflow: 'auto', // scroll interno si hace falta
          }}
        />
      </Draggable>
    );
  },
);

export default function ReservationFormDialog(props: {
  open: boolean;
  mode: Mode;
  initialValues?: any;
  reservaId?: number;
  onClose: () => void;
  children?: React.ReactNode;
  readOnly?: boolean;
}) {
  const { open, mode, initialValues, reservaId, onClose, children, readOnly = false } = props;

  const { t } = useTranslation<'translation'>();

  const title =
    mode === 'create'
      ? t('reservation.dialog.new')
      : t('reservation.dialog.existing', { id: reservaId });

  // Normaliza initialValues para compatibilidad con el nuevo modelo de estados.
  const formInitialValues = useMemo(() => {
    if (!initialValues) return initialValues;
    const iv = { ...initialValues } as any;

    // Compat: normalizar campos de estado
    if (!iv.estado) {
      if (iv.estado_code) iv.estado = iv.estado_code;
      else if (iv.estadoCode) iv.estado = iv.estadoCode;
    }

    // Compat: normalizar shadowing / idProfesorShadow desde distintas formas que puede devolver la API
    iv.idProfesorShadow =
      iv.idProfesorShadow ??
      iv.id_profesor_shadow ??
      iv.id_profesorShadow ??
      iv.shadowingInfo?.profesor?.id ??
      iv.shadowing?.profesor?.id ??
      iv.shadowing?.profesorId ??
      iv.shadowing?.profesor_id ??
      undefined;

    // Asegurar que shadowingInfo existe si viene anidado en otras claves
    if (!iv.shadowingInfo && iv.shadowing && typeof iv.shadowing === 'object') {
      iv.shadowingInfo = iv.shadowing;
    }

    // Si hay idProfesorShadow, marcar shadowing true para que el formulario muestre el checkbox
    if ((iv.idProfesorShadow || iv.shadowingInfo) && iv.shadowing == null) {
      iv.shadowing = true;
    }

    return iv;
  }, [initialValues]);

  /* =========================================================
     FORZAR REMOUNT DEL FORMULARIO EN APERTURA / CAMBIO DE ID
     ========================================================= */
  const [instance, setInstance] = useState(0);

  // Detecta flanco de apertura (false -> true)
  const prevOpenRef = useRef<boolean>(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setInstance((i) => i + 1);
    }
    prevOpenRef.current = open;
  }, [open]);

  // Si cambia la "firma" de edición mientras está abierto, también remonta
  const sig = `${mode}:${reservaId ?? 'new'}`;
  const prevSigRef = useRef<string>(sig);
  useEffect(() => {
    if (open && sig !== prevSigRef.current) {
      setInstance((i) => i + 1);
    }
    prevSigRef.current = sig;
  }, [sig, open]);

  const formKey = useMemo(() => `${sig}:${instance}`, [sig, instance]);

  /* ============================================
     Clase online (resolver enlace centro+asign.)
     ============================================ */
  const toNum = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const centroId = useMemo(
    () =>
      toNum(
        formInitialValues?.idCentro ?? formInitialValues?.centroId ?? formInitialValues?.id_centro,
      ),
    [formInitialValues],
  );

  const asignaturaId = useMemo(
    () =>
      toNum(
        formInitialValues?.idAsignatura ??
          formInitialValues?.asignaturaId ??
          formInitialValues?.id_asignatura,
      ),
    [formInitialValues],
  );

  const [taLoading, setTaLoading] = useState(false);
  const [taUrl, setTaUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchTaUrl() {
      if (!open || !centroId || !asignaturaId) {
        setTaUrl(null);
        return;
      }
      setTaLoading(true);
      try {
        const res = await api.get('/tutorias-aplicadas/links/resolve', {
          params: { centroId, asignaturaId },
          // ✅ 404 se considera “respuesta válida” (no dispara el interceptor de error)
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
          // Opcional: si tu interceptor mira un header para saltarse toasts
          // headers: { 'x-skip-error-toast': '1' },
        });

        if (cancelled) return;

        if (res.status === 404) {
          // No hay enlace configurado → ocultamos el botón
          setTaUrl(null);
        } else {
          setTaUrl(res.data?.url ?? null);
        }
      } finally {
        if (!cancelled) setTaLoading(false);
      }
    }

    fetchTaUrl();
    return () => {
      cancelled = true;
    };
  }, [open, centroId, asignaturaId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperComponent={ReservationDialogPaper}
      keepMounted
    >
      <DialogTitle id="reservation-dialog-title" sx={{ cursor: 'move' }}>
        {title}
      </DialogTitle>

      <DialogContent dividers sx={{ position: 'relative' }}>
        {/* Capa bloqueadora solo lectura */}
        {readOnly && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              pointerEvents: 'all',
              background: 'transparent',
            }}
          />
        )}

        {/* Botón "Clase online" (aparece si hay link resuelto) */}
        {taUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 11, // por encima de la capa readOnly
              pointerEvents: 'auto',
            }}
          >
            <Button
              variant="contained"
              disabled={taLoading || !taUrl}
              onClick={() => taUrl && window.open(taUrl, '_blank', 'noopener,noreferrer')}
              sx={{
                backgroundColor: '#21a598',
                '&:hover': { backgroundColor: '#1a8578' },
                boxShadow: 'none',
              }}
            >
              Clase online
            </Button>
          </Box>
        )}

        {formInitialValues ? (
          <ReservationForm
            key={formKey}
            mode={mode}
            initialValues={formInitialValues}
            reservaId={reservaId}
            onSaved={onClose}
            onCancel={onClose}
          />
        ) : (
          children
        )}
      </DialogContent>
    </Dialog>
  );
}
